'use client';

import { Heart, Bed, Bath, Car, Maximize2, MapPin, Building2, TrendingUp } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { formatPrice as formatPriceUtil } from '@/lib/supabase';

interface PropertyCardProps {
  property: {
    id: string;
    slug: string;
    title: string;
    card_title?: string | null;
    project_name?: string | null;
    projectName?: string | null;
    price: number;
    bedrooms: number | null;
    bathrooms: number | null;
    parking?: number | null;
    floor_area?: number | null;
    lot_area?: number | null;
    floor_number?: number | null;
    furnishing?: string | null;
    featured_image_url?: string | null;
    images_urls?: string[] | null;
    city?: string | null;
    listing_type?: string | null;
    property_type?: string | null;
  };
}

export default function PropertyCardHorizontal({ property }: PropertyCardProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get bedroom label
  const getBedroomLabel = (bedrooms: number | null) => {
    if (bedrooms === null || bedrooms === 0) return 'Studio';
    return `${bedrooms} BR`;
  };

  // Get furnishing badge color
  const getFurnishingColor = (furnishing?: string) => {
    switch (furnishing?.toLowerCase()) {
      case 'fully furnished':
      case 'fully':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'semi furnished':
      case 'semi':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'bare':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Get image URL
  const getImageUrl = () => {
    if (imageError) return '/images/placeholders/bgc-condo.jpg';
    if (property.featured_image_url) return property.featured_image_url;
    if (property.images_urls && property.images_urls.length > 0) return property.images_urls[0];
    return '/images/placeholders/bgc-condo.jpg';
  };

  // Safety check: if no slug, don't render invalid links
  if (!property.slug) {
    console.warn('Property missing slug:', property.id, property.title)
    return null
  }

  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#d60500] hover:shadow-xl transition-all duration-300">
      <div className="flex flex-col md:flex-row">
        {/* Image Container - Left side on desktop, top on mobile */}
        <div className="relative h-64 md:h-auto md:w-2/5 overflow-hidden bg-gray-100 flex-shrink-0">
          <Link href={`/property/${property.slug}`}>
            <Image
              src={getImageUrl()}
              alt={property.title}
              fill
              className="object-cover group-hover:scale-110 transition-transform duration-500"
              onError={() => setImageError(true)}
            />
          </Link>

          {/* Overlay Badges */}
          <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
            {/* Listing Type Badge */}
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-sm ${
              property.listing_type === 'For Sale' || property.listing_type === 'for_sale'
                ? 'bg-[#d60500] text-white border-[#d60500]'
                : 'bg-blue-500 text-white border-blue-500'
            }`}>
              {property.listing_type === 'for_sale' || property.listing_type === 'For Sale' ? 'For Sale' :
               property.listing_type === 'for_rent' || property.listing_type === 'For Rent' ? 'For Rent' :
               property.listing_type || 'For Sale'}
            </span>

            {/* Like Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                setIsLiked(!isLiked);
              }}
              className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-lg"
            >
              <Heart
                className={`w-5 h-5 ${
                  isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'
                } transition-colors`}
              />
            </button>
          </div>

          {/* Furnishing Badge */}
          {property.furnishing && (
            <div className={`absolute bottom-4 left-4 px-3 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-sm shadow-lg ${getFurnishingColor(property.furnishing)}`}>
              {property.furnishing}
            </div>
          )}

          {/* Spire Logo Badge */}
          <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-200">
            <Image
              src="/spire-logo-black.webp"
              alt="Spire Group"
              width={60}
              height={20}
              className="h-3 w-auto"
            />
          </div>
        </div>

        {/* Content - Right side on desktop, bottom on mobile */}
        <div className="flex-1 p-6">
          <div className="flex flex-col h-full">
            {/* Top Section */}
            <div className="flex-1">
              {/* Price */}
              <div className="mb-3">
                <div className="text-2xl md:text-3xl font-bold text-[#d60500] mb-1 font-heading">
                  {formatPriceUtil(property.price)}
                  {(property.listing_type === 'For Rent' || property.listing_type === 'for_rent') && <span className="text-sm font-light text-gray-500">/month</span>}
                </div>
              </div>

              {/* Title: Card Title (e.g. "2BR Condo – BGC") */}
              <Link href={`/property/${property.slug}`}>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-[#d60500] transition-colors font-heading">
                  {property.card_title || property.projectName || property.project_name || property.city || 'Property'}
                </h3>
              </Link>

              {/* City & Property Type */}
              <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 font-light">
                {property.city && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {property.city.replace(/ City$/i, '')}
                  </span>
                )}
                {property.property_type && (
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    {property.property_type === 'house_and_lot' || property.property_type === 'house_&_lot' ? 'House & Lot' :
                     property.property_type === 'condo' ? 'Condo' :
                     property.property_type === 'townhouse' ? 'Townhouse' :
                     property.property_type === 'lot' ? 'Lot' :
                     property.property_type === 'office_space' ? 'Office Space' :
                     property.property_type === 'commercial' ? 'Commercial' :
                     property.property_type === 'warehouse' ? 'Warehouse' :
                     property.property_type === 'farm' ? 'Farm' :
                     property.property_type === 'resort' ? 'Resort' :
                     property.property_type === 'parking' ? 'Parking' :
                     property.property_type === 'beach' ? 'Beach Lot' :
                     property.property_type === 'land' ? 'Land' :
                     property.property_type}
                  </span>
                )}
              </div>

              {/* Icon Details Row ONLY (Match Homepage Style) */}
              <div className="flex flex-wrap items-center gap-6 text-gray-500 text-sm font-light pb-4 border-b border-gray-100 mb-4">
                {/* Bedrooms */}
                {property.bedrooms !== null && property.bedrooms > 0 && (
                  <span className="flex items-center gap-2">
                    <Bed className="w-4 h-4 text-gray-400" />
                    {property.bedrooms} Beds
                  </span>
                )}

                {/* Bathrooms */}
                {property.bathrooms !== null && property.bathrooms > 0 && (
                  <span className="flex items-center gap-2">
                    <Bath className="w-4 h-4 text-gray-400" />
                    {property.bathrooms} Baths
                  </span>
                )}

                {/* Floor Area - Always show */}
                {property.floor_area && (
                  <span className="flex items-center gap-2">
                    <Maximize2 className="w-4 h-4 text-gray-400" />
                    {property.floor_area} sqm
                  </span>
                )}

                {/* Lot Area - Show for House & Lot IN ADDITION to floor area */}
                {(property.property_type === 'house_and_lot' || property.property_type === 'house_&_lot') && property.lot_area && (
                  <span className="flex items-center gap-2">
                    <Maximize2 className="w-4 h-4 text-gray-400" />
                    {property.lot_area} sqm lot
                  </span>
                )}

                {/* Parking */}
                {property.parking && property.parking > 0 && (
                  <span className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-gray-400" />
                    {property.parking} Parking
                  </span>
                )}
              </div>

              {/* Floor Level */}
              {property.floor_number && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg mt-4 inline-flex">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Floor {property.floor_number}</span>
                  </span>
                </div>
              )}
            </div>

            {/* CTA Button - Bottom */}
            <div className="mt-4">
              <Link href={`/property/${property.slug}`}>
                <button className="w-full md:w-auto bg-[#d60500] hover:bg-[#b50400] text-white font-semibold py-3 px-6 rounded-none transition-all flex items-center justify-center gap-2 group/btn tracking-wider text-sm uppercase">
                  View Property Details
                  <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
